import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { MatStepper } from "@angular/material/stepper";
import { NavigationExtras, Router } from "@angular/router";
import { User } from "@core/models";
import { LetItGoType } from "@core/models/letitgo-type";
import { TrainsInfo } from "@core/models/trainsInfo";
import {
  BookedService,
  NgxToastrService,
  TrainService,
  TravelUtilsService,
  UtilsService,
  AuthService,
  LanguageService,
} from "@core/services";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { UserCardModalComponent } from "@shared/component/user-card-modal/user-card-modal.component";
import moment from "moment";
import { Subscription } from "rxjs";
import Swal from "sweetalert2";
import swal from "sweetalert2";

@Component({
  selector: "app-confirm-booking",
  templateUrl: "./confirm-booking.component.html",
  styleUrls: ["./confirm-booking.component.scss"],
})
export class ConfirmBookingComponent implements OnInit, OnDestroy {
  @Input() user: User | undefined;
  @Input() selectedUsers: User[] | undefined;
  @Input() stepper: MatStepper | undefined;
  @Input() selectedTrains: any;
  totalPrice: number = 0


  @Input() isMocked: boolean | undefined;
  @Input() params: any;
  @Input() outwardSeats: any;
  @Input() returnSeats: any;
  @Input() isUser: any;
  @Input() isAdmin: boolean | undefined;
  @Input() userType: string | undefined;
  @Input() amount: any;
  @Input() farePriceMap: any;
  @Input() canRequest: boolean | undefined;
  @Input() trainsComplements: any;
  @Input() bookFailed: boolean | undefined;
  @Input() projectData: any;
  extraInfoForm: UntypedFormGroup | undefined;
  navigationExtras: any;
  userFilters: any = {};

  trainsList: any;
  userUuid: string | undefined;
  isEmployee: boolean = false;
  userToBook: string | undefined;
  canUseTRM: boolean = true;
  trainsInfo: TrainsInfo | null = {} as TrainsInfo;
  subscription: Subscription;
  priceChanged

  constructor(
    public trainService: TrainService,
    private travelUtilsService: TravelUtilsService,
    private utilsService: UtilsService,
    private translate: TranslateService,
    private router: Router,
    private modalService: NgbModal,
    private bookingService: BookedService,
    private authService: AuthService,
    private ngxToastrService: NgxToastrService,
    private fb: UntypedFormBuilder,
    private langService: LanguageService,
  ) {
    this.subscription = this.trainService.getTrainsInfo$().subscribe((info: TrainsInfo | null) => {
      this.trainsInfo = info;
    });
    this.utilsService.socket.on("reserve-trains", (res) => {
      this.reserveTrainsResponse(res);
    });
  }

  ngOnDestroy(): void {
    this.utilsService.socket.off('reserve-trains');
    this.subscription.unsubscribe()
  }

  ngOnInit(): void {
    this.extraInfoForm = this.fb.group({
      customCode: [null],
      costCenter: [null],
      travellerReason: [null],
      annotationsExtras: [null],
    });
  }

  cancel() {
    history.back();
  }

  showPaymentCardSelector(): Promise<any> {
    if (this.trainsInfo?.company.bookedWithoutCard) {
      return Promise.resolve(null);
    }

    if (this.trainsInfo?.user && !Array.isArray(this.trainsInfo?.user.paymentCards))
      this.trainsInfo.user.paymentCards = this.trainsInfo?.user.paymentCards ? [this.trainsInfo?.user.paymentCards] : [];

    /* if (!Array.isArray(this.user.personalPaymentCards)) {
      this.user.personalPaymentCards = this.user.personalPaymentCards ? [this.user.personalPaymentCards] : [];
    } */

    return new Promise((resolve) => {
      const modalRef = this.modalService.open(UserCardModalComponent, {
        windowClass: 'custom-modal-user-card',
        centered: true,
        backdrop: 'static',
        keyboard: false
      });
      modalRef.componentInstance.bookCards = this.trainsInfo?.user.paymentCards;
      /* modalRef.componentInstance.bookPersonalCards = this.user.personalPaymentCards; */
      modalRef.componentInstance.fromBookSelect = true;
      modalRef.componentInstance.bookType = 'train';
      modalRef.componentInstance.user = this.user;
      modalRef.componentInstance.company = this.trainsInfo?.company;
      modalRef.componentInstance.cardSelected.subscribe((selectedCard) => {
        resolve(selectedCard);
      });
      modalRef.result.catch(() => {
        resolve(null);
      });
    });
  }

  onReserve() {
    this.showPaymentCardSelector().then((selectedCard) => {
      if (!selectedCard && !this.trainsInfo?.company.bookedWithoutCard) {
        return;
      }
      this.utilsService.loadingTrains = true;
      const correlation = crypto.randomUUID();
      let request = {
        language: this.langService.getLanguage(),
        userUuid: this.trainsInfo?.userUuid,
        itineraryId: this.trainsInfo?.itineraryId,
        booker: this.trainsInfo?.travellersForm.value[0],
        passengers: this.trainsInfo?.travellersForm.value,
        reservability: this.trainsInfo?.reservability,
        token: this.trainsInfo?.token,
        correlationId: correlation,
        customCode: this.extraInfoForm?.value.customCode,
        costCenter: this.extraInfoForm?.value.costCenter,
        annotationsExtras: this.extraInfoForm?.value.annotationsExtras,
        trainsInfo: {
          selectedExtras: this.trainsInfo?.selectedExtras,
          selectedSeats: this.trainsInfo?.selectedSeats,
          selectedTrains: this.trainsInfo?.selectedTrains,
          searchConfig: this.trainsInfo?.searchConfig,
          price: {
            total: this.trainService.totalPrice(this.trainsInfo ?? {} as TrainsInfo),
            outward: {
              finalPrice: this.trainsInfo?.selectedTrains.selectedOutwardTrain.finalPrice * 1,
              iva: this.trainsInfo?.selectedTrains.selectedOutwardTrain.iva * 1,
              finalCommision: this.trainsInfo?.selectedTrains.selectedOutwardTrain.finalCommision * 1,
              providerPrice: this.trainsInfo?.selectedTrains.selectedOutwardTrain.price * 1,
              extras: 0,
              seats: 0,
            },
          },
        }
      }
      if (this.trainsInfo?.selectedExtras?.outward && Array.isArray(this.trainsInfo?.selectedExtras?.outward)) {
        const totalExtras = this.trainsInfo?.selectedExtras?.outward.reduce((acc, extra) => acc + (extra.price * extra.quantity || 0), 0);
        request.trainsInfo.price.outward.extras = totalExtras || 0;
      }
      if (this.trainsInfo?.selectedSeats?.outward && Array.isArray(this.trainsInfo?.selectedSeats?.outward)) {
        const totalSeats = this.trainsInfo?.selectedSeats?.outward.reduce((acc, seat) => acc + (seat.passengersPrice[0].price || 0), 0);
        request.trainsInfo.price.outward.seats = totalSeats || 0;
      }
      if (this.trainsInfo?.selectedTrains.selectedReturnAlternatives) {
        request.trainsInfo.price['return'] = {
          finalPrice: this.trainsInfo?.selectedTrains.selectedReturnTrain.finalPrice * 1,
          iva: this.trainsInfo?.selectedTrains.selectedReturnTrain.iva * 1,
          finalCommision: this.trainsInfo?.selectedTrains.selectedReturnTrain.finalCommision * 1,
          providerPrice: this.trainsInfo?.selectedTrains.selectedReturnTrain.price * 1,
          extras: 0,
          seats: 0,
        }
        if (this.trainsInfo?.selectedExtras?.return && Array.isArray(this.trainsInfo?.selectedExtras?.return)) {
          const totalExtras = this.trainsInfo?.selectedExtras?.return.reduce((acc, extra) => acc + (extra.price * extra.quantity || 0), 0);
          request.trainsInfo.price['return'].extras = totalExtras || 0;
        }
        if (this.trainsInfo?.selectedSeats?.return && Array.isArray(this.trainsInfo?.selectedSeats?.return)) {
          const totalSeats = this.trainsInfo?.selectedSeats?.return.reduce((acc, seat) => acc + (seat.passengersPrice[0].price || 0), 0);
          request.trainsInfo.price['return'].seats = totalSeats || 0;
        }
      }
      if (!this.trainsInfo?.company.bookedWithoutCard) {
        request.trainsInfo["paymentCard"] = {
          name: selectedCard.name,
          token: selectedCard.token
        };
      }
      this.trainService.addCorrelation(correlation);
      this.trainService.reserveTrains(request).subscribe(
        (s) => {
          console.log(s)
        },
        (err) => {
          console.log(err)
          this.letItGo()
          const navigationExtras: NavigationExtras = {
            state: {
              module: "trains",
            },

          };
          this.authService.totalAuthorizations++;
          this.router.navigate([`/my-bookeds`], navigationExtras);
        })
    })
  }

  requestAuthorization() {
    this.utilsService.loadingTrains = true;
    const correlation = crypto.randomUUID();

    let request = {
      language: this.langService.getLanguage(),
      userUuid: this.trainsInfo?.userUuid,
      itineraryId: this.trainsInfo?.itineraryId,
      booker: this.trainsInfo?.travellersForm.value[0],
      passengers: this.trainsInfo?.travellersForm.value,
      reservability: this.trainsInfo?.reservability,
      token: this.trainsInfo?.token,
      correlationId: correlation,
      customCode: this.extraInfoForm?.value.customCode,
      costCenter: this.extraInfoForm?.value.costCenter,
      annotationsExtras: this.extraInfoForm?.value.annotationsExtras,
      trainsInfo: {
        selectedExtras: this.trainsInfo?.selectedExtras,
        selectedSeats: this.trainsInfo?.selectedSeats,
        selectedTrains: this.trainsInfo?.selectedTrains,
        searchConfig: this.trainsInfo?.searchConfig,
        price: {
          total: this.trainService.totalPrice(this.trainsInfo ?? {} as TrainsInfo),
          outward: {
            finalPrice: this.trainsInfo?.selectedTrains.selectedOutwardTrain.finalPrice * 1,
            iva: this.trainsInfo?.selectedTrains.selectedOutwardTrain.iva * 1,
            finalCommision: this.trainsInfo?.selectedTrains.selectedOutwardTrain.finalCommision * 1,
            providerPrice: this.trainsInfo?.selectedTrains.selectedOutwardTrain.price * 1,
            extras: 0,
            seats: 0,
          },
        },
      }
    }
    if (this.trainsInfo?.selectedExtras?.outward && Array.isArray(this.trainsInfo?.selectedExtras?.outward)) {
      const totalExtras = this.trainsInfo?.selectedExtras?.outward.reduce((acc, extra) => acc + (extra.price || 0), 0);
      request.trainsInfo.price.outward.extras = totalExtras || 0;
    }
    if (this.trainsInfo?.selectedSeats?.outward && Array.isArray(this.trainsInfo?.selectedSeats?.outward)) {
      const totalSeats = this.trainsInfo?.selectedSeats?.outward.reduce((acc, extra) => acc + (extra.price || 0), 0);
      request.trainsInfo.price.outward.seats = totalSeats || 0;
    }
    if (this.trainsInfo?.selectedTrains.selectedReturnAlternatives) {
      request.trainsInfo.price['return'] = {
        finalPrice: this.trainsInfo?.selectedTrains.selectedReturnTrain.finalPrice * 1,
        iva: this.trainsInfo?.selectedTrains.selectedReturnTrain.iva * 1,
        finalCommision: this.trainsInfo?.selectedTrains.selectedReturnTrain.finalCommision * 1,
        providerPrice: this.trainsInfo?.selectedTrains.selectedReturnTrain.price * 1,
        extras: 0,
        seats: 0,
      }
      if (this.trainsInfo?.selectedExtras?.return && Array.isArray(this.trainsInfo?.selectedExtras?.return)) {
        const totalExtras = this.trainsInfo?.selectedExtras?.return.reduce((acc, extra) => acc + (extra.price || 0), 0);
        request.trainsInfo.price['return'].extras = totalExtras || 0;
      }
      if (this.trainsInfo?.selectedSeats?.return && Array.isArray(this.trainsInfo?.selectedSeats?.return)) {
        const totalSeats = this.trainsInfo?.selectedSeats?.return.reduce((acc, extra) => acc + (extra.price || 0), 0);
        request.trainsInfo.price['return'].seats = totalSeats || 0;
      }
    }
    this.trainService.addCorrelation(correlation);

    const authorization = {
      type: "train",
      price: this.trainService.totalPrice(this.trainsInfo ?? {} as TrainsInfo),
      requestData: request,
      fromDate: `${this.utilsService.formatDate(this.trainsInfo?.searchConfig?.departDate).toISOString()}`,
      toDate:
        this.trainsInfo?.searchConfig?.returnDate != null
          ? `${this.utilsService.formatDate(this.trainsInfo?.searchConfig.returnDate).toISOString()}`
          : "",
      customCode: this.extraInfoForm?.value.customCode,
      costCenter: this.extraInfoForm?.value.costCenter,
      travellerReason: this.extraInfoForm?.value.travellerReason,
      annotationsExtras: this.extraInfoForm?.value.annotationsExtras,
    };
    if (this.trainsInfo?.userUuid) {
      authorization["userUuid"] = this.trainsInfo?.userUuid;
    }
    this.bookingService.requestAuthorization(authorization).subscribe(
      () => {
        this.utilsService.loadingTrains = false;
        this.translate
          .get(["common.congrat", "authorization.train"])
          .subscribe((response) => {
            swal
              .fire({
                title: response["common.congrat"],
                text: response["authorization.train"],
                position: "center",
                showConfirmButton: false,
                icon: "success",
                timer: 5000,
              })
              .then(() => {
                const navigationExtras: NavigationExtras = {
                  state: {
                    module: "trains",
                  },

                };
                this.authService.totalAuthorizations++;
                this.router.navigate([`/my-bookeds`], navigationExtras);
              });
            if (response.Amount != null && response.Amount.isNan()) {
              swal.close();
              this.utilsService.loadingTrains = false;
              this.translate.get("authorization.error").subscribe((resp) => {
                this.ngxToastrService.typeError(resp, "error");
              });
            }
          });
      },
      (err) => {
        if (err.status === 403 && err.error?.message === "This action is not allowed in this plan") {
          this.translate.get("authorization.not-allowed-plan").subscribe((translatedMessage) => {
            this.ngxToastrService.typeInfo('', translatedMessage);
          });
          // modalRef.close();
        }
        swal.close();
        this.utilsService.loadingTrains = false;
        this.letItGo()
        const navigationExtras: NavigationExtras = {
          state: {
            module: "trains",
          },

        };
        this.authService.totalAuthorizations++;
        this.router.navigate([`/my-bookeds`], navigationExtras);
      }
    )
  }

  letItGo() {
    const goingStops = this.trainsInfo?.selectedTrains?.selectedOutwardTrain?.sections
    let goingText = "Tramo ida:";
    if (goingStops.length > 1) {
      goingText = "Tramos ida:";
    }

    const body = {
      // quedan tarjetasde desciento
      travellers: this.trainsInfo?.travellers.map(
        (u) => `${u.name} ${u.lastname}, `
      ),
      itinerary: `${this.trainsInfo?.searchConfig?.origin.name} - ${this.trainsInfo?.searchConfig?.destination.name}`,
      dates: `${moment(this.trainsInfo?.selectedTrains.selectedOutwardTrain.departDate).format("DD/MM/YYYY HH:mm")}`,
      stopsText: goingStops.length > 0 ?
        `${goingText} ${goingStops
          .map((seg, index) => {
            let text = `${seg.origin}-${seg.destination} (Carrier: ${seg.carrier || "N/A"}, tarifa: ${seg.fareName || "N/A"}, tren número: ${seg.trainNumber || "N/A"}), llega a ${seg.destination} a las ${moment(seg.arriveDate).format("DD/MM/YYYY HH:mm")}`
            if (goingStops[index + 1]) text += `, sale hacia ${goingStops[index + 1].origin} a las ${moment(goingStops[index + 1].departDate).format("DD/MM/YYYY HH:mm")}`;
            return text
          }).join(", ")}` : `${goingText} ninguna`,
      price: this.trainService.totalPrice(this.trainsInfo ?? {} as TrainsInfo),
      stops: this.trainsInfo?.selectedTrains?.selectedOutwardTrain?.sections.length,
      seats: {outward: this.trainsInfo?.selectedSeats?.outward.map((s) =>
      ({
        carriage: s.carriage,
        position: s.position,
        itinerary: this.trainsInfo?.selectedTrains?.selectedOutwardTrain?.sections.filter((section) => section.sectionId === s.sectionId)?.map((section) => `${section.origin} - ${section.destination}`)[0]
      })), return: [] as {
          carriage: string;
          position: string;
          itinerary: string;
        }[]},
      extras: {outward: this.trainsInfo?.selectedExtras?.outward.map((s) =>
      ({
        description: s.description,
        quantity: s.quantity,
        itinerary: this.trainsInfo?.selectedTrains?.selectedOutwardTrain?.sections.filter((section) => section.sectionId === s.sectionId)?.map((section) => `${section.origin} - ${section.destination}`)[0]
      })), return: [] as {
          description: string;
          quantity: string|number;
          itinerary: string;
        }[]},
      customCode: this.extraInfoForm?.value.customCode,
      costCenter: this.extraInfoForm?.value.costCenter,
      annotationsExtras: this.extraInfoForm?.value.annotationsExtras,
    };
    if (this.trainsInfo?.selectedTrains?.selectedReturnTrain) {
      const returnDate = moment(this.trainsInfo?.selectedTrains?.selectedReturnTrain?.departDate).format("DD/MM/YYYY HH:mm")
      body.dates += " - " + `${returnDate}`;
      body.stops += this.trainsInfo?.selectedTrains?.selectedReturnTrain?.sections.length
      this.trainsInfo?.selectedSeats?.return?.forEach((s) => {
        body.seats.return.push({
          carriage: s.carriage,
          position: s.position,
          itinerary: this.trainsInfo?.selectedTrains?.selectedReturnTrain?.sections.filter((section) => section.sectionId === s.sectionId)?.map((section) => `${section.origin} - ${section.destination}`)[0]
        })
      })
      this.trainsInfo?.selectedExtras.return?.forEach((s) => {
        body.extras.return.push({
          description: s.description,
          quantity: s.quantity,
          itinerary: this.trainsInfo?.selectedTrains?.selectedReturnTrain?.sections.filter((section) => section.sectionId === s.sectionId)?.map((section) => `${section.origin} - ${section.destination}`)[0]
        })
      })
      const returningStops = this.trainsInfo?.selectedTrains?.selectedReturnTrain?.sections
      let returningText = " Tramo vuelta:";
      if (returningStops.length > 1) {
        returningText = " Tramos vuelta:";
      }
      body.stopsText += returningStops.length > 0 ?
        `${returningText} ${returningStops
          .map((seg, index) => {
            let text = `${seg.origin}-${seg.destination} (Carrier: ${seg.carrier || "N/A"}, tarifa: ${seg.fareName || "N/A"}, tren número: ${seg.trainNumber || "N/A"}), llega a ${seg.destination} a las ${moment(seg.arriveDate).format("DD/MM/YYYY HH:mm")}`
            if (goingStops[index + 1]) text +=`, sale hacia ${goingStops[index + 1].origin} a las ${moment(goingStops[index + 1].departDate).format("DD/MM/YYYY HH:mm")}`;
            return text
          }).join(", ")}` : `${returningText} ninguna`
    }
    this.travelUtilsService.removeTravelLocalStorage();

    if (this.trainsInfo?.searchConfig?.project && this.trainsInfo?.searchConfig?.project.uuid !== "none") {
      body["project"] = this.trainsInfo?.searchConfig?.project.title || "";
    }

    this.utilsService.sendEmailOperations(
      LetItGoType.train,
      body,
      this.trainsInfo?.user?.uuid
    );
    this.translate
      .get(["common.congrat", "common.operations"])
      .subscribe((response) => {
        swal
          .fire({
            position: "center",
            icon: "success",
            title: response["common.congrat"],
            text: response["common.operations"],
            showConfirmButton: false,
            timer: this.utilsService.letItGoTimer,
            timerProgressBar: true,
          })
          .then(() => {
            this.utilsService.loadingTrains = false;
          });
      });
  }

  reserveTrainsResponse(res) {
    if (this.trainService.existsCorrelation(res.correlationId)) {
      this.trainService.removeCorrelation(res.correlationId)
      this.utilsService.loadingTrains = false;
      if ((res.data.type && (res.data.type?.toLowerCase().includes("error") || res.data.type?.toLowerCase().includes("exception"))) || (res.data.bookingStatus && (res.data.bookingStatus?.toLowerCase().includes('unknown') || res.data.bookingStatus?.toLowerCase().includes('error')))) {
        if (res.data.status === 410 && res.data.message === "Sold Out") {
          this.translate
            .get([
              "train.error.sold-out",
            ])
            .subscribe((response) => {
              Swal.fire({
                position: "center",
                imageUrl:
                  "../../../../../assets/img/svg/check-circle.svg",
                title: '',
                text: response[
                  "train.error.sold-out"
                ],
                showConfirmButton: false,
                timer: 10000,
              })
            })
        } else if (res.data?.status === 818) {
          this.translate
            .get(["train.notAvailable"])
            .subscribe((result) => {
              this.ngxToastrService.typeInfo(
                "Error",
                result["train.notAvailable"]
              );
            })
        } else if (res?.data?.message?.toLowerCase().includes('invalid card') && this.trainsInfo?.searchConfig?.travellers.some((t: any) => t.selectedCard)) {
          this.translate
            .get(["train.invalid-card"])
            .subscribe((result) => {
              this.ngxToastrService.typeInfo(
                'Error',
                'train.invalid-card'
              );
            })
        } else if (res?.data?.status === 409 && res.data.message === "Price changed") {
          this.translate
            .get([
              "flight.detail-flight.price-changed",
              "flight.detail-flight.previous-price",
              "flight.detail-flight.current-price",
              "flight.detail-flight.change-detected",
              "project.accept",
              "common.cancel",
            ])
            .subscribe((translations) => {
              swal.fire({
                icon: "warning",
                title: translations["flight.detail-flight.change-detected"],
                html: `
                  <p>${translations["flight.detail-flight.price-changed"]}</p>
                  <p><strong>${translations["flight.detail-flight.previous-price"]}:</strong> ${this.trainService.calculateFinalPrice(this.trainsInfo as TrainsInfo)} €</p>
                  <p><strong>${translations["flight.detail-flight.current-price"]}:</strong> ${res.data.content.finalPrice} €</p>
                `,
                showCancelButton: true,
                confirmButtonText: translations["project.accept"],
                cancelButtonText: translations["common.cancel"],
                customClass: {
                  popup: "price-change-popup",
                  confirmButton: "simple-confirm-btn",
                  cancelButton: "simple-cancel-btn",
                },
                buttonsStyling: false,
              }).then((result) => {
                if (result.isConfirmed) {
                  this.priceChanged = res.data.content.finalPrice;
                  this.showPaymentCardSelector().then((selectedCard) => {
                    if (!selectedCard && !this.trainsInfo?.company.bookedWithoutCard) {
                      return;
                    }
                    this.utilsService.loadingTrains = true;
                    const correlation = res.correlationId;
                    let request = {
                      language: this.langService.getLanguage(),
                      userUuid: this.trainsInfo?.userUuid,
                      itineraryId: this.trainsInfo?.itineraryId,
                      booker: this.trainsInfo?.travellersForm.value[0],
                      passengers: this.trainsInfo?.travellersForm.value,
                      reservability: this.trainsInfo?.reservability,
                      token: this.trainsInfo?.token,
                      correlationId: correlation,
                      customCode: this.extraInfoForm?.value.customCode,
                      costCenter: this.extraInfoForm?.value.costCenter,
                      annotationsExtras: this.extraInfoForm?.value.annotationsExtras,
                      trainsInfo: {
                        selectedExtras: this.trainsInfo?.selectedExtras,
                        selectedSeats: this.trainsInfo?.selectedSeats,
                        selectedTrains: this.trainsInfo?.selectedTrains,
                        searchConfig: this.trainsInfo?.searchConfig,
                        price: {
                          total: res.data.content.finalPrice,
                          outward: {
                            finalPrice: this.trainsInfo?.selectedTrains.selectedOutwardTrain.finalPrice * 1,
                            iva: this.trainsInfo?.selectedTrains.selectedOutwardTrain.iva * 1,
                            finalCommision: this.trainsInfo?.selectedTrains.selectedOutwardTrain.finalCommision * 1,
                            providerPrice: this.trainsInfo?.selectedTrains.selectedOutwardTrain.price * 1,
                            extras: 0,
                            seats: 0,
                          },
                        },
                      }
                    }
                    if (this.trainsInfo?.selectedExtras?.outward && Array.isArray(this.trainsInfo?.selectedExtras?.outward)) {
                      const totalExtras = this.trainsInfo?.selectedExtras?.outward.reduce((acc, extra) => acc + (extra.price * extra.quantity || 0), 0);
                      request.trainsInfo.price.outward.extras = totalExtras || 0;
                    }
                    if (this.trainsInfo?.selectedSeats?.outward && Array.isArray(this.trainsInfo?.selectedSeats?.outward)) {
                      const totalSeats = this.trainsInfo?.selectedSeats?.outward.reduce((acc, seat) => acc + (seat.passengersPrice[0].price || 0), 0);
                      request.trainsInfo.price.outward.seats = totalSeats || 0;
                    }
                    if (this.trainsInfo?.selectedTrains.selectedReturnAlternatives) {
                      request.trainsInfo.price['return'] = {
                        finalPrice: this.trainsInfo?.selectedTrains.selectedReturnTrain.finalPrice * 1,
                        iva: this.trainsInfo?.selectedTrains.selectedReturnTrain.iva * 1,
                        finalCommision: this.trainsInfo?.selectedTrains.selectedReturnTrain.finalCommision * 1,
                        providerPrice: this.trainsInfo?.selectedTrains.selectedReturnTrain.price * 1,
                        extras: 0,
                        seats: 0,
                      }
                      if (this.trainsInfo?.selectedExtras?.return && Array.isArray(this.trainsInfo?.selectedExtras?.return)) {
                        const totalExtras = this.trainsInfo?.selectedExtras?.return.reduce((acc, extra) => acc + (extra.price * extra.quantity || 0), 0);
                        request.trainsInfo.price['return'].extras = totalExtras || 0;
                      }
                      if (this.trainsInfo?.selectedSeats?.return && Array.isArray(this.trainsInfo?.selectedSeats?.return)) {
                        const totalSeats = this.trainsInfo?.selectedSeats?.return.reduce((acc, seat) => acc + (seat.passengersPrice[0].price || 0), 0);
                        request.trainsInfo.price['return'].seats = totalSeats || 0;
                      }
                    }
                    if (!this.trainsInfo?.company.bookedWithoutCard) {
                      request.trainsInfo["paymentCard"] = {
                        name: selectedCard.name,
                        token: selectedCard.token
                      };
                    }
                    this.trainService.addCorrelation(correlation);
                    this.trainService.createReservation(request).subscribe(
                      (s) => {
                        console.log(s)
                      },
                      (err) => {
                        console.log(err)
                        this.letItGo()
                        const navigationExtras: NavigationExtras = {
                          state: {
                            module: "trains",
                          },

                        };
                        this.authService.totalAuthorizations++;
                        this.router.navigate([`/my-bookeds`], navigationExtras);
                      }
                    )
                  })
                } else {
                  this.utilsService.loadingTrains = false;
                }
              });
            });
        } else {
          this.subscription = this.trainService.getTrainsInfo$().subscribe((info: TrainsInfo | null) => {
            if (!info) return;
            this.trainsInfo = info;
            this.letItGo()
            if (this.trainsInfo?.searchConfig?.project && this.trainsInfo?.searchConfig.project.uuid !== "none") {
              res.forEach((booking: any) => {
                this.addToTravel(booking);
              });
            }
            this.trainService.clearTrainsInfo();
            const navigationExtras: NavigationExtras = {
              state: {
                module: "trains",
              },
            };
            if (!info.isAdmin && !info.isEmployee) {
              this.router.navigate([`/my-bookeds`], navigationExtras);
            } else {
              this.router.navigate(
                [`/user/booked/${info.userUuid}`],
                navigationExtras
              );
            }
          });
        }
      } else {
        console.log(res)
        this.translate
          .get(["common.congrat", "train.success"])
          .subscribe((response) => {
            this.utilsService.loadingTrains = false;
            swal
              .fire({
                position: "center",
                icon: "success",
                title: response["common.congrat"],
                text: response["train.success"],
                showConfirmButton: false,
                timer: 3000,
              })
              .then(() => {
                this.subscription = this.trainService.getTrainsInfo$().subscribe((info: TrainsInfo | null) => {
                  if (!info) return;
                  this.trainsInfo = info;
                  if (this.trainsInfo?.searchConfig?.project && this.trainsInfo?.searchConfig.project.uuid !== "none") {
                    res.forEach((booking: any) => {
                      this.addToTravel(booking);
                    });
                  }
                  this.trainService.clearTrainsInfo();
                  const navigationExtras: NavigationExtras = {
                    state: {
                      module: "trains",
                    },
                  };
                  if (!info?.isAdmin && !info?.isEmployee) {
                    this.router.navigate([`/my-bookeds`], navigationExtras);
                  } else {
                    this.router.navigate(
                      [`/user/booked/${info.userUuid}`],
                      navigationExtras
                    );
                  }
                });
              });
          });
      }
    }
  }

  addToTravel(train: any) {
    if (this.utilsService.hasProcessedUuid(train.uuid)) {
      return;
    }
    this.utilsService.addProcessedUuid(train.uuid);

    const checkInAux = moment(train.departDate).format("YYYY-MM-DD");
    const addBody: any = {
      item: {
        type: "train",
        uuid: train.uuid,
        initDate: checkInAux,
      },
    };
    if (train.returnDate != null) {
      const checkOutAux = moment(train.returnDate).format("YYYY-MM-DD");
      addBody.item.endDate = checkOutAux;
    }
    this.travelUtilsService.addItemToTravel(addBody, this.projectData.selectedProject.uuid);
  }

  cleanData() {
    this.trainsInfo = {} as TrainsInfo;;
  }

  isFareWithinRange(): boolean {
    if (
      !this.trainsInfo?.userFilters ||
      this.trainsInfo?.userFilters?.priceMinTrain == null ||
      this.trainsInfo?.userFilters?.priceMaxTrain == null
    ) {
      return true;
    }
    return (
      this.totalPrice >= this.trainsInfo?.userFilters.priceMinTrain &&
      this.totalPrice <= this.trainsInfo?.userFilters.priceMaxTrain
    );
  }
}
